import type { ReactNode } from "react";
import type { InputParam } from "../../types/grid.ts";
import type { InitCache } from "../../types/session.ts";
import type { ToastVariant } from "../ui/toast.tsx";
import type { ChoiceDialogOptions } from "../../hooks/useAlertDialog.tsx";
import { deleteGridCell } from "../../api/endpoints.ts";

export interface CellDecoratorContext {
  rowData: unknown[];
  colIndex: Record<string, number>;
  colName: string;
  rawValue: unknown;
}

export type CellDecorator = (node: ReactNode, ctx: CellDecoratorContext) => ReactNode;

export interface CellEditCommitContext {
  gridName: string;
  rowData: unknown[];
  colIndex: Record<string, number>;
  /** Column names that uniquely identify the row (server `KeyColumns`). */
  keyColumns: string[];
  /**
   * Pre-built Equal-only filter that uniquely identifies the row, ready to
   * pass to `DeleteGridCell` etc. In pivot mode, includes the
   * `PivotHeaderColumn=<edited header>` entry so the filter resolves to the
   * underlying row, not the visible pivot cell.
   */
  keyFilter: InputParam[];
  colName: string;
  newValue: unknown;
  oldValue: unknown;
}

export type CellCommitResult =
  | { kind: "default" }
  | { kind: "skip" }
  | { kind: "replace"; value: unknown; extraParams?: InputParam[] }
  /**
   * Update the cell's displayed value locally without an API round-trip.
   * Use for staging-style grids (e.g. CompuShopRestock) where the user edits
   * a transient view that's later consumed wholesale by a button-triggered SP.
   * `value` defaults to the committed newValue.
   */
  | { kind: "local"; value?: unknown };

export interface CellConfirmContext {
  rowData: unknown[];
  colIndex: Record<string, number>;
  colName: string;
  rawValue: unknown;
}

export interface CellConfirmContent {
  title?: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
}

/** Per-column override for the CHECKBOXCONFIRM/DATELOCK confirm dialog content. */
export type CellConfirmBuilder = (ctx: CellConfirmContext) => CellConfirmContent | null;

/**
 * Per-cell editability gate. Returning true demotes the cell to non-editable
 * for *this row only* even when the column is editable at column level — used
 * for one-shot operations like "Approve payout" where, once set, the row must
 * not be re-toggled. The grid skips the inline-edit / checkbox affordances
 * and falls through to the standard renderer (decorators still apply).
 */
export type CellLockedBuilder = (ctx: CellConfirmContext) => boolean;

export interface GridOverride {
  /** Per-column wrapper applied around the rendered cell value. Lookup is case-insensitive. */
  cellDecorators?: Record<string, CellDecorator>;
  /**
   * Per-column custom confirm dialog content for CHECKBOXCONFIRM/DATELOCK
   * toggle. Returning null falls back to the generic "Are you sure?" copy.
   * Lookup is case-insensitive.
   */
  cellConfirms?: Record<string, CellConfirmBuilder>;
  /**
   * Per-cell editability gate (row-aware). Used for one-shot operations:
   * once a row's value satisfies the predicate, the cell is rendered
   * read-only even though the column itself is editable. Lookup is
   * case-insensitive.
   */
  cellLocked?: Record<string, CellLockedBuilder>;
  /**
   * Pre-save hook called before updateGridCell. Return:
   *  - undefined / { kind: "default" } → proceed normally
   *  - { kind: "skip" } → abort save (e.g. user cancelled in a confirm dialog)
   *  - { kind: "replace", value, extraParams? } → save with a different value or extra InputParams
   */
  onCellEditCommit?: (
    ctx: CellEditCommitContext,
  ) => Promise<CellCommitResult | void> | CellCommitResult | void;
}

/**
 * Runtime services injected into registry builders by DataGrid. Builders need
 * these because their behavior often involves UI prompts or grid refresh —
 * resources only available inside the React tree at runtime.
 */
export interface GridOverrideDeps {
  choose: <T>(opts: ChoiceDialogOptions<T>) => Promise<T | null>;
  toast: (msg: string, variant?: ToastVariant) => void;
  refresh: () => void;
  cache: InitCache | null;
  /** Open a PagePopup over the current grid. Same path syntax the
   *  hyperlinkDispatcher uses (e.g. `searcheditproduct?edit=123`,
   *  `packagedeal?edit=42`). Lets a cellDecorator route clicks through
   *  the existing popup machinery instead of hand-rolling navigation. */
  openPopup: (path: string) => void;
}

export type GridOverrideBuilder = (deps: GridOverrideDeps) => GridOverride;

/**
 * Central registry: grid name (lowercase) → builder. DataGrid calls the
 * builder with runtime deps (dialog, toast, refresh, cache) and merges the
 * result with any per-call `gridOverride` prop. Pages may also pass overrides
 * directly via the prop; prop entries win on conflict.
 */
const builders: Record<string, GridOverrideBuilder> = {};

export function registerGridOverride(name: string, builder: GridOverrideBuilder): void {
  builders[name.toLowerCase()] = builder;
}

export function getGridOverrideBuilder(name: string): GridOverrideBuilder | undefined {
  return builders[name.toLowerCase()];
}

export function getCellDecorator(
  override: GridOverride | undefined,
  colName: string,
): CellDecorator | undefined {
  if (!override?.cellDecorators) return undefined;
  const map = override.cellDecorators;
  if (colName in map) return map[colName];
  const lc = colName.toLowerCase();
  for (const k of Object.keys(map)) {
    if (k.toLowerCase() === lc) return map[k];
  }
  return undefined;
}

export function getCellConfirmBuilder(
  override: GridOverride | undefined,
  colName: string,
): CellConfirmBuilder | undefined {
  if (!override?.cellConfirms) return undefined;
  const map = override.cellConfirms;
  if (colName in map) return map[colName];
  const lc = colName.toLowerCase();
  for (const k of Object.keys(map)) {
    if (k.toLowerCase() === lc) return map[k];
  }
  return undefined;
}

export function getCellLockedBuilder(
  override: GridOverride | undefined,
  colName: string,
): CellLockedBuilder | undefined {
  if (!override?.cellLocked) return undefined;
  const map = override.cellLocked;
  if (colName in map) return map[colName];
  const lc = colName.toLowerCase();
  for (const k of Object.keys(map)) {
    if (k.toLowerCase() === lc) return map[k];
  }
  return undefined;
}

// ── Built-in builders ─────────────────────────────────────────────────────

function lookup(rowData: unknown[], colIndex: Record<string, number>, ...candidates: string[]): unknown {
  const wanted = new Set(candidates.map((c) => c.toLowerCase()));
  for (const k of Object.keys(colIndex)) {
    if (wanted.has(k.toLowerCase())) {
      const idx = colIndex[k];
      if (idx !== undefined) return rowData[idx];
    }
  }
  return undefined;
}

type DeleteChoice = "remove" | "empty";

/**
 * WebAdmin2-parity localization delete prompt: clearing a cell to empty/null
 * on a LOCALIZATION* grid asks whether to remove the override row entirely
 * (`DeleteGridCell` keyed by row's KeyColumns) or persist the empty value via
 * the default UpdateGridCell. Cancel aborts the save.
 */
function localizationDeleteOverride({ choose, toast, refresh, cache }: GridOverrideDeps): GridOverride {
  return {
    onCellEditCommit: async (ctx) => {
      if (ctx.newValue !== "" && ctx.newValue !== null) return { kind: "default" };

      const keyword = String(lookup(ctx.rowData, ctx.colIndex, "sKeyword") ?? "");
      const langRaw = String(lookup(ctx.rowData, ctx.colIndex, "sLanguage") ?? "");
      const langDict = (cache?.CommonData as { LanguageDictionary?: Record<string, { Header?: string }> } | undefined)
        ?.LanguageDictionary;
      const language = langDict?.[langRaw.toLowerCase()]?.Header ?? langRaw;
      const groupBy = String(lookup(ctx.rowData, ctx.colIndex, "sGroupBy", "GroupBy") ?? "");
      const groupValue = String(lookup(ctx.rowData, ctx.colIndex, "sGroupValue", "GroupValue") ?? "");

      const choice = await choose<DeleteChoice>({
        title: "Confirm Localization Deletion",
        body: (
          <div>
            <p>Do you want to remove the override on</p>
            <p>
              <strong>{keyword || ctx.colName}</strong>
              {groupBy && groupValue ? <> — {groupBy}: {groupValue}</> : null}
              {language ? <> — {language}</> : null}
            </p>
            <p>or override using an empty string?</p>
          </div>
        ),
        options: [
          { label: "Remove", value: "remove", variant: "danger" },
          { label: "Empty", value: "empty", variant: "primary", default: true },
        ],
      });
      if (choice === null) return { kind: "skip" };
      if (choice === "empty") return { kind: "default" };

      try {
        const result = await deleteGridCell(ctx.gridName, ctx.keyFilter);
        if (!result.bError && result.oData) {
          refresh();
        } else {
          toast(result.sMessage || "Failed to delete localization row", "error");
        }
      } catch {
        toast("Failed to delete localization row", "error");
      }
      return { kind: "skip" };
    },
  };
}

registerGridOverride("LocalizationDefault", localizationDeleteOverride);
registerGridOverride("LocalizationOperatorGroup", localizationDeleteOverride);
registerGridOverride("LocalizationGroup", localizationDeleteOverride);

/**
 * CompuShopRestock is a staging grid: edits update the visible row in place
 * but are NOT persisted. A separate "Create Restock" button reads the grid's
 * current state and runs an SP that consumes the staged values. WebAdmin2
 * handled this in onUpdatedCell by returning early after a local mutation.
 */
function compuShopRestockOverride(): GridOverride {
  return {
    onCellEditCommit: () => ({ kind: "local" }),
  };
}

registerGridOverride("CompuShopRestock", compuShopRestockOverride);
